/**
 * The Orders half of the API surface.
 *
 * Every call goes through `@/lib/api`, which owns the cookie, the CSRF and origin headers,
 * the Idempotency-Key on every mutation, RFC 9457 problem parsing and the step-up retry.
 * Nothing in this file talks to `fetch`.
 *
 * Two reads feed the whole feature: `GET /orders/page` is the screen and
 * `GET /orders/:id/detail` is the drawer. The rest are the writes, one function per route,
 * named after the button that fires it.
 */
import { api, API_BASE } from '@/lib/api';
import { ulid } from '@/lib/ulid';
import type { OrderDrawerDetailView, OrdersPageResponseView } from './types';
import type {
  AutoCancelExtendResult,
  BulkReprintOutcome,
  CompOrderRequest,
  HoldReason,
  LineEditDiff,
  LineEditRequest,
  MarkPaidRequest,
  OrderAddressEditRequest,
  OrderAddressEditResult,
  OrderCreditResult,
  OrderReprintResult,
  PrintDoc,
  RefundRequest,
  TimelineEntry,
  LegendResponse,
} from '@elutide/shared';

export interface BulkResult {
  batch_id: string;
  results: { order_id: number; ok: boolean; skipped: boolean; error: string | null }[];
}

export interface BulkPreview {
  action: string;
  affected: { order_id: number; eligible: boolean; reason: string | null }[];
  warnings: string[];
}

export interface SendReminderResult {
  reminder_id: string;
  sent: boolean;
  skipped_reason: string | null;
}

export type OrdersPageQuery = Record<string, string | number | boolean | undefined>;

export const ordersApi = {
  /* ---- reads ------------------------------------------------------- */

  /** The whole screen: tiles, queue counts, rows, unmatched payments, the widen notice. */
  page: (query: OrdersPageQuery) => api.get<OrdersPageResponseView>('/orders/page', { query }),

  /** The whole drawer, one round trip. */
  detail: (orderId: number) => api.get<OrderDrawerDetailView>(`/orders/${orderId}/detail`),

  /** Every definition the console publishes, for the hover popovers. */
  legend: () => api.get<LegendResponse>('/legend'),

  /* ---- print ------------------------------------------------------- */

  /**
   * Reprint through the HMAC bridge.
   *
   * The Idempotency-Key is the bridge command id, so a retry after a timeout replays the
   * stored response instead of printing a second label. The key is passed in rather than
   * generated inside the client because the bulk fan-out needs one key per order, held
   * across that order's own retries and shared with no other order.
   */
  reprint: (orderId: number, docs: readonly PrintDoc[], idempotencyKey: string) =>
    api.post<OrderReprintResult>(
      `/orders/${orderId}/reprint`,
      { docs: [...docs] },
      { idempotencyKey },
    ),

  /** First print. Asks the queue for artifacts that already exist. */
  print: (orderId: number, docs: readonly PrintDoc[]) =>
    api.post<unknown>(`/orders/${orderId}/print`, { docs: [...docs], reprint: false }),

  /* ---- money ------------------------------------------------------- */

  markPaid: (orderId: number, body: MarkPaidRequest) =>
    api.post<unknown>(`/orders/${orderId}/mark-paid`, body),

  refund: (orderId: number, body: RefundRequest) =>
    api.post<unknown>(`/orders/${orderId}/refund`, body),

  cancel: (orderId: number, body: { expected_version: string; reason: string; restock: boolean }) =>
    api.post<unknown>(`/orders/${orderId}/cancel`, body),

  comp: (body: CompOrderRequest) => api.post<{ order_id: number }>('/orders/comp', body),

  credit: (orderId: number, body: { amount_cents: number; reason: string }) =>
    api.post<OrderCreditResult>(`/orders/${orderId}/credit`, body),

  previewLines: (orderId: number, body: LineEditRequest) =>
    api.post<LineEditDiff>(`/orders/${orderId}/lines/preview`, body),

  commitLines: (orderId: number, body: LineEditRequest) =>
    api.put<unknown>(`/orders/${orderId}/lines`, body),

  /* ---- payment window ---------------------------------------------- */

  /** Push the pending auto-cancel action out. `hours` omitted takes the configured step. */
  extendAutoCancel: (orderId: number, body: { hours?: number; note: string | null }) =>
    api.post<AutoCancelExtendResult>(`/orders/${orderId}/autocancel/extend`, body),

  sendReminder: (orderId: number, channel: 'email' | 'sms') =>
    api.post<SendReminderResult>(`/orders/${orderId}/reminders`, { channel }),

  /* ---- fulfilment -------------------------------------------------- */

  updateTracking: (
    orderId: number,
    body: { carrier: string; tracking_number: string; notify_customer: boolean },
  ) => api.put<unknown>(`/orders/${orderId}/tracking`, body),

  changeStatus: (
    orderId: number,
    body: { expected_version: string; ops_state: string; reason: string },
  ) => api.post<unknown>(`/orders/${orderId}/status`, body),

  /* ---- customer ---------------------------------------------------- */

  editAddress: (orderId: number, body: OrderAddressEditRequest) =>
    api.put<OrderAddressEditResult>(`/orders/${orderId}/address`, body),

  /* ---- notes, holds and flags -------------------------------------- */

  addNote: (orderId: number, body: string, customerVisible: boolean) =>
    api.post<TimelineEntry>(`/orders/${orderId}/notes`, {
      body,
      customer_visible: customerVisible,
    }),

  placeHold: (orderId: number, reason: HoldReason, note: string | null) =>
    api.post<unknown>(`/orders/${orderId}/hold`, { reason, note, notify_customer: false }),

  releaseHold: (orderId: number, reason: string) =>
    api.post<unknown>(`/orders/${orderId}/hold/release`, { reason }),

  flagAddress: (orderId: number, note: string) =>
    api.post<unknown>(`/orders/${orderId}/flag-address`, { note }),

  /* ---- bulk -------------------------------------------------------- */

  bulkPreview: (action: string, orderIds: readonly number[]) =>
    api.post<BulkPreview>('/orders/bulk/preview', { action, order_ids: [...orderIds] }),

  bulk: (
    action: string,
    orderIds: readonly number[],
    params: Record<string, unknown>,
    reason: string,
  ) => api.post<BulkResult>('/orders/bulk', { action, order_ids: [...orderIds], params, reason }),
};

/**
 * Reprint several orders, one request each.
 *
 * There is no bulk reprint on the server and there should not be: the bridge memoises a
 * reprint per order under its command id, so the only shape that is safe to retry is one
 * order per key. This fans out with a fresh ULID per order, keeps every outcome including
 * the failures, and never lets one order's 500 stop the rest.
 *
 * Sequential rather than parallel on purpose. Fifty simultaneous reprints is a rate limit
 * response and a print queue full of jobs in an order nobody chose; fifty in a row is a
 * few seconds and a predictable stack of paper.
 */
export async function reprintMany(
  orderIds: readonly number[],
  docs: readonly PrintDoc[],
  deps: { reprint?: typeof ordersApi.reprint; newKey?: () => string } = {},
): Promise<BulkReprintOutcome[]> {
  const reprint = deps.reprint ?? ordersApi.reprint;
  const newKey = deps.newKey ?? ulid;
  const outcomes: BulkReprintOutcome[] = [];

  for (const orderId of orderIds) {
    try {
      const result = await reprint(orderId, docs, newKey());
      outcomes.push({
        order_id: orderId,
        ok: result.failed.length === 0,
        replayed: result.replayed,
        accepted: result.accepted,
        error:
          result.failed.length === 0
            ? null
            : result.failed.map((entry) => `${entry.doc}: ${entry.reason}`).join(', '),
      });
    } catch (error) {
      outcomes.push({
        order_id: orderId,
        ok: false,
        replayed: false,
        accepted: [],
        error: error instanceof Error ? error.message : 'The reprint failed.',
      });
    }
  }

  return outcomes;
}

/**
 * A PackCam photo.
 *
 * Built here rather than trusted from the response so the drawer cannot be talked into
 * loading an image from somewhere else by a field in a payload. The uuid is the only part
 * that varies and the server validates it against this order twice before it becomes a
 * path.
 */
export function packPhotoUrl(orderId: number, uuid: string, size: 'thumb' | 'orig'): string {
  return `${API_BASE}/orders/${orderId}/photos/${encodeURIComponent(uuid)}?size=${size}`;
}
